import { createSelector } from '@reduxjs/toolkit'
import { denormalize, normalize } from 'normalizr'
import { useSelector } from 'react-redux'
import { get, pick } from 'lodash'
import api from '../../api'
import config from '../../config'
import { missingOrdersSchema } from '../normalizr'
import { getPaginationHeaders, useAsyncAction } from '../utils'
import createSlice from '../create-slice'
import { getEntities } from './entities'

const MISSING_KEYS = [
  'cycleId',
  'missingQuery',
  'missingPage',
  'missingPageSize',
]

const missingOrdersInitialState = {
  cycles: [],
}

const missingOrdersSlice = createSlice({
  name: 'missingOrders',
  initialState: missingOrdersInitialState,
  reducers: {
    setMissingOrders: (state, { payload: { result, params, total } }) => {
      const queryKey = JSON.stringify(params)

      state.missingOrders = state.missingOrders || {}
      state.missingOrders[queryKey] = {
        ids: result,
        total,
      }
    },
  },
})

const { actions } = missingOrdersSlice

const selectState = state => state.missingOrders

function getMissingOrderUrl({ filePath }) {
  if (!filePath) return null
  const path = filePath.startsWith('/') ? filePath : `/${filePath}`
  return `${config.apiUrl}${path}`
}

const selectMissingOrders = createSelector(
  selectState,
  getEntities,
  (state, params) => params,
  (state, entities, params) => {
    const queryKey = JSON.stringify(params)

    const resp = {
      missingOrders: denormalize(
        get(state.missingOrders, [queryKey, 'ids'], []),
        [missingOrdersSchema],
        entities,
      ).map(missingOrder => ({
        ...missingOrder,
        url: getMissingOrderUrl(`orders/${params.cycleId}/missing-orders`),
      })),
      total: get(state.missingOrders, [queryKey, 'total'], 0),
    }
    return resp
  },
)

export function useMissingOrders(conditions) {
  return useSelector(state => selectMissingOrders(state, conditions))
}

function fetchMissingOrders(params) {
  return async dispatch => {
    const { body, headers } = await api.orders.listMissingOrders(
      pick(params, MISSING_KEYS),
    )

    return dispatch(
      actions.setMissingOrders({
        params: pick(params, MISSING_KEYS),
        ...normalize(body, [missingOrdersSchema]),
        ...getPaginationHeaders(headers),
      }),
    )
  }
}

export function useFetchMissingOrders(params) {
  return useAsyncAction(fetchMissingOrders, params)
}

export default missingOrdersSlice
